import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Users, Award, Globe, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function AboutSection({ texts }) {
  const stats = [
    { icon: Users, emoji: "🏆", ...texts.stat1, color: "from-blue-500 to-cyan-500" },
    { icon: Award, emoji: "🏁", ...texts.stat2, color: "from-purple-500 to-pink-500" },
    { icon: Globe, emoji: "🌍", ...texts.stat3, color: "from-green-500 to-teal-500" }
  ];

  return (
    //<section id="about" className="py-24 bg-gray-950/30 relative z-10">
    <section id="about" className="py-24 relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="bg-gray-800 text-purple-400 border-purple-500/20 mb-4">
            {texts.badge}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {texts.title1}
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {texts.title2}
            </span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-3xl font-bold text-white mb-6">
              {texts.subtitle1}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> {texts.subtitle2}</span>
            </h3>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              {texts.p1}
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              {texts.p2}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-6"
          >
            {texts.values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-3 p-5 rounded-xl bg-gray-900/50 border border-gray-800"
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300 leading-relaxed">{value}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-900/50 border-gray-800 hover:border-gray-700 transition-all duration-500 group rounded-2xl h-full">
                <CardContent className="px-10 pb-10 pt-10 text-center h-full">
                  <div className={`w-16 h-16 mx-auto mb-8 rounded-2xl bg-gradient-to-r ${stat.color} bg-opacity-10 border border-gray-700 flex items-center justify-center mt-1`}>
                    <span className="text-3xl">{stat.emoji}</span>
                  </div>
                  <div className={`text-4xl font-bold mb-3 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                    {stat.value}
                  </div>
                  <p className="text-gray-400 font-medium">{stat.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
